/**
 * Upsell banner — shown inline when a user on a lower tier tries to use
 * a feature gated to a higher tier (e.g. a Starter user picking the
 * /commentary slash skill which requires Growth).
 *
 * Soft block: V1 does NOT actually prevent the underlying call — the
 * banner displays and the action is held until the user clicks either
 * "Talk to us" (opens an upgrade email) or dismisses. We don't run the
 * skill on dismiss either; the user has to retry deliberately. This
 * keeps the gate honest without hard-failing legitimate work.
 *
 * Hidden behind a portal-less floating div over the chat area, so it
 * doesn't disrupt the existing scroll position.
 */
import React from 'react';
import { PlanTier } from '../services/authProfile';
import { TIER_LABEL } from '../services/featureGates';

interface Props {
  featureName: string;
  requiredTier: PlanTier;
  currentTier: PlanTier | null;
  contactEmail: string;
  onDismiss: () => void;
}

export function UpsellBanner({ featureName, requiredTier, currentTier, contactEmail, onDismiss }: Props): JSX.Element {
  const currentLabel = currentTier ? TIER_LABEL[currentTier] : 'your current plan';
  const targetLabel = TIER_LABEL[requiredTier];

  const subject = `Practice IQ Reporting — Upgrade to ${targetLabel}`;
  const body = [
    `Hi Practice IQ team,`,
    ``,
    `I'm on the ${currentLabel} plan and would like to upgrade to ${targetLabel} so I can use ${featureName}.`,
    ``,
    `Please let me know the next steps.`,
    ``,
    `Thanks!`,
  ].join('\n');
  const mailtoHref = `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <div style={s.wrap} role="alertdialog" aria-label="Upgrade required">
      <div style={s.icon}>🔒</div>
      <div style={s.body}>
        <div style={s.title}>
          <b>{featureName}</b> is a <b>{targetLabel}</b> feature.
        </div>
        <div style={s.sub}>
          You're on {currentLabel}. Upgrade to unlock.
        </div>
        <div style={s.actions}>
          <a href={mailtoHref} style={s.cta}>Talk to us →</a>
          <button onClick={onDismiss} style={s.dismiss}>Maybe later</button>
        </div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  wrap: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    background: '#fff7e6',
    border: '1px solid #f4d68a',
    borderRadius: 6,
    padding: '10px 12px',
    margin: '8px 12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  icon: {
    fontSize: 18,
    lineHeight: 1,
    marginTop: 2,
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 13,
    color: '#1f1f1f',
    lineHeight: 1.4,
  },
  sub: {
    fontSize: 11,
    color: '#666',
    marginTop: 2,
    marginBottom: 8,
  },
  actions: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },
  cta: {
    background: '#1F3864',
    color: '#fff',
    padding: '5px 12px',
    borderRadius: 4,
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: 12,
  },
  dismiss: {
    background: 'transparent',
    border: 'none',
    color: '#666',
    fontSize: 12,
    cursor: 'pointer',
    padding: '5px 4px',
    textDecoration: 'underline',
  },
};
